import React from 'react';
import { useChatWidgetContext } from '../../../context/chat-widget-context/chatWidgetContext';
import { ChatWidgetStatus, DeviceType } from '../../../context/chat-widget-context/types';
import { Container, CloseButton } from './EnhancedFloatingButtons.style';
import { FloatingButtonActionType } from './types';
import { useFalconContext } from '../../../context/cms-context/FalconContext';
import { TextButtonBaseProps } from '@oneaudi/unified-web-components/dist/cjs/components/text-button';
import { AnimatedActionButton } from './AnimatedActionButton';
import useFloatingButtonUtils from './hooks/useFloatingButtonUtils';
import { useSalesforceIframeObserver } from '../../../hooks/useSalesforceIframeObserver';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { runSetFloatingButtonsHidden } from '../../../context/chat-widget-context/reducers/actions/runs';
import { FLOATING_BUTTONS_DISMISSED_KEY } from '../../../constants';
import safeWindow from '../../../utils/safeWindows';

export const EnhancedFloatingButtons: React.FC = () => {
  const { cmsContent } = useFalconContext();
  const { state, launchChat, sendTextMessage, dispatch } = useChatWidgetContext();
  const [isHovered, setIsHovered] = React.useState(false);

  useDeviceType();

  useSalesforceIframeObserver({
    rightOffset: cmsContent.chatWidget.widgetChatWindowHorizontalPosition,
    deviceType: state.deviceType,
  });

  const { shouldShowFloatingButtons, memoizedButtonVisibilityCondition } = useFloatingButtonUtils();

  const isError = state.status === ChatWidgetStatus.ERROR;
  const isDisabled = isError || state.status === ChatWidgetStatus.LOADING;

  const currentPosition = state.isWindowOpen
    ? state.floatingButtonsPosition.open
    : state.floatingButtonsPosition.closed;

  const handleAMessageAction = (type: FloatingButtonActionType, value: string) => {
    if (type === FloatingButtonActionType.CHAT_MESSAGE) {
      if (isDisabled) return;
      launchChat();
      //? INFO: Delay to ensure the chat is ready to receive the message after launch
      setTimeout(() => sendTextMessage(value), 800);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(runSetFloatingButtonsHidden(true));
    safeWindow.sessionStorage?.setItem(FLOATING_BUTTONS_DISMISSED_KEY, 'true');
  };

  const isTouchDevice =
    state.deviceType === DeviceType.MOBILE || state.deviceType === DeviceType.TABLET;
  const isCloseButtonVisible = isTouchDevice || isHovered;

  return (
    <Container
      position={currentPosition}
      isVisible={shouldShowFloatingButtons}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CloseButton
        isVisible={isCloseButtonVisible && shouldShowFloatingButtons}
        itemPosition={state.position}
        onClick={handleClose}
        aria-label="Cerrar botones flotantes"
      />
      {/* {isError && <ErrorText>Error de conexión con el chat</ErrorText>} */}

      {cmsContent.floatingButtons.buttons.map((config, index) => {
        return (
          <AnimatedActionButton
            key={index}
            position={state.position}
            isDisabled={config.type === FloatingButtonActionType.CHAT_MESSAGE && isDisabled}
            onClick={() => handleAMessageAction(config.type, config.value)}
            variant={
              config.type === FloatingButtonActionType.CHAT_MESSAGE ? 'secondary' : 'primary'
            }
            href={config.type === FloatingButtonActionType.EXTERNAL_LINK ? config.value : undefined}
            size={'xs' as TextButtonBaseProps['size']}
            isVisible={shouldShowFloatingButtons && memoizedButtonVisibilityCondition(config)}
            animationDelay={`${index * 0.1}s`}
            type={config.type}
          >
            {config.label}
          </AnimatedActionButton>
        );
      })}
    </Container>
  );
};
